import { all, call, put, select, takeLatest } from "redux-saga/effects";
import * as types from "../actions/actionTypes";
import {
  getAllManufacturersResponse,
  getManufacturerByIdResponse,
  getManufacturerDashboardResponse,
  getManufacturerProfileResponse,
} from "../actions";
import { axiosGet, axiosPut } from "../../utils/common/axios";
import {
  createEntitySagas,
  extractPayloadAndCallback,
  notifyError,
  notifySuccess,
  selectCompanyId,
  toQuery,
} from "./sagaHelpers";

const COMPUTED = ["engagements", "activeEngagements", "walletBalance", "availableBalance", "heldAmount", "pendingDecisions", "activeOrders", "lastActivity"];

export const manufacturerEntity = createEntitySagas({
  entity: "manufacturer",
  endpoint: "manufacturers",
  label: "Manufacturer",
  scope: "company",
  injectCompanyId: true,
  computedFields: COMPUTED,
  selectSlice: (state) => state.manufacturer,
  listKey: "manufacturers",
  types: {
    getAll: types.GET_ALL_MANUFACTURERS,
    getById: types.GET_MANUFACTURER_BY_ID,
    create: types.CREATE_MANUFACTURER,
    update: types.UPDATE_MANUFACTURER,
    remove: types.DELETE_MANUFACTURER,
  },
  actions: { getAllResponse: getAllManufacturersResponse, getByIdResponse: getManufacturerByIdResponse },
});

// One company is expected to own exactly one manufacturer record (unlike brands, where many
// brands legitimately share their manufacturer's companyId - see getBrandProfileSaga), so
// "manufacturers?companyId=X" -> first row is safe for the SSO/admin login path, which has no
// manufacturer id of its own to key off (the SSO session is a company-level identity, not a
// per-manufacturer one). The local manufacturer/password login *does* carry its own id
// (loginUser.manufacturer.id) - prefer that when it's there so a future non-1:1 company can't
// silently show the wrong profile the way brands did.
function* getManufacturerProfileSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  try {
    const auth = yield select((state) => state.auth);
    const manufacturerId = payload?.id ?? auth?.loginUser?.manufacturer?.id;
    const companyId = yield* selectCompanyId(payload);
    if (!companyId && !manufacturerId) {
      yield put(getManufacturerProfileResponse(null));
      callback?.success?.(null);
      return;
    }
    let profile;
    if (manufacturerId) {
      ({ data: profile } = yield call(axiosGet, `manufacturers/${manufacturerId}${toQuery({ companyId })}`));
    } else {
      const { data } = yield call(axiosGet, `manufacturers${toQuery({ companyId })}`);
      profile = Array.isArray(data) && data.length ? data[0] : null;
    }
    yield put(getManufacturerProfileResponse(profile));
    if (profile && payload?.withDashboard) {
      const res = yield call(axiosGet, `manufacturers/${profile.id}/dashboard`);
      yield put(getManufacturerDashboardResponse(res.data));
    }
    callback?.success?.(profile);
  } catch (e) {
    notifyError(e, "Could not load manufacturer profile");
    callback?.failure?.(e);
  }
}

function* getManufacturerDashboardSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  try {
    const { data } = yield call(axiosGet, `manufacturers/${payload.id}/dashboard`);
    yield put(getManufacturerDashboardResponse(data));
    callback?.success?.(data);
  } catch (e) {
    yield put(getManufacturerDashboardResponse(null));
    notifyError(e, "Could not load dashboard");
    callback?.failure?.(e);
  }
}

// PUT is a full overwrite, so the whole stored profile is sent back with the edited fields.
function* updateManufacturerProfileSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  const previous = yield select((state) => state.manufacturer.profile);
  if (!previous) return;
  try {
    yield put(getManufacturerProfileResponse({ ...previous, ...payload }));
    const body = { ...previous, ...payload, id: previous.id };
    COMPUTED.forEach((f) => delete body[f]);
    const { data } = yield call(axiosPut, `manufacturers/${previous.id}`, body);
    const computed = Object.fromEntries(COMPUTED.map((f) => [f, previous[f]]));
    yield put(getManufacturerProfileResponse({ ...data, ...computed }));
    notifySuccess("Settings saved");
    callback?.success?.(data);
  } catch (e) {
    yield put(getManufacturerProfileResponse(previous));
    notifyError(e, "Could not save settings");
    callback?.failure?.(e);
  }
}

export function* manufacturerSaga() {
  yield all([
    call(manufacturerEntity.watcher),
    takeLatest(types.GET_MANUFACTURER_PROFILE, getManufacturerProfileSaga),
    takeLatest(types.GET_MANUFACTURER_DASHBOARD, getManufacturerDashboardSaga),
    takeLatest(types.UPDATE_MANUFACTURER_PROFILE, updateManufacturerProfileSaga),
  ]);
}
